import React from 'react';
import { useRootSelector } from 'common/features/featuresReducer';
import { RootState } from 'rootStore';

import ResponsiveContainer, {
    ContainerType,
} from 'components/responsive-containers/ResponsiveContainer';
import { MobileSortAndFilterView } from './MobileSortAndFilterView';
import { Card, isCardReloadable } from 'common/api/e-comm/models/Card';
import { ECommBrand } from 'common/api/e-comm/models/ECommBrand';
import { WalletSortAndFilterView } from './context';
import { CardWithBrand, Option, FilterGroup } from './types';
import { FilterAndSortSection } from './FilterAndSortSection';
import { allCards as allCardsOption, eGift, reload } from './util';
import { ProductType } from 'common/api/e-comm/models/Product';
import { createSelector } from 'reselect';
import { PartialRecord } from 'common/definitions/PartialRecord';

interface WalletFilterProps {
    allCards: Card[];
    setViewableCards: React.Dispatch<React.SetStateAction<Card[]>>;
    result: number;
    shouldResetFilters: boolean;
    shouldPersistFilters: boolean;
}

const brandExistsInWallet = (allCards: Card[], id: number) => {
    return allCards.some(({ brandId }: Card) => brandId === id);
};

const getBrandsAndCards = createSelector(
    (state: RootState) => state.store.brands.brandsById,
    (_: RootState, allCards: Card[]) => allCards,
    (brandsById: PartialRecord<number, ECommBrand>, allCards: Card[]) => {
        const brands_ = Object.values(brandsById || {}) as ECommBrand[];
        return {
            brands: brands_
                .filter(({ id }: ECommBrand) => brandExistsInWallet(allCards, id))
                .sort((brand1: ECommBrand, brand2: ECommBrand) =>
                    brand1.name.localeCompare(brand2.name)
                )
                .map(
                    ({ name, id, imageUrl }: ECommBrand) =>
                        ({
                            text: name,
                            value: id.toString(),
                            imageUrl,
                            group: FilterGroup.brand,
                        } as Option)
                ) as Option[],
            cardsWithBrand: allCards.map((card: Card) => {
                return {
                    ...card,
                    type: isCardReloadable(card) ? ProductType.Reload : card.type,
                    brand: brandsById[card.brandId],
                };
            }) as CardWithBrand[],
        };
    }
);

export const WalletSortAndFilter = (props: WalletFilterProps) => {
    const { allCards, result, setViewableCards, shouldResetFilters, shouldPersistFilters } = props;
    /*
     * Builds 2 data-structures, brands and cardsWithBrand
     * - brands data structure is used to represent the filters in the UI for rendering
     * - cardsWithBrand data structure is used to do the actual filtering when user selects
     *   different brands. The Card data structure is insuficient as it only contains the
     *   the `brandId`.
     *
     *  NOTE: For brands we check if the brand has an associated card. The reason for this
     *  is that brands come from `brandsById`, that state slice is actually shared by PDP page.
     *  The PDP page _loads_ the brand into the `brandsById`
     */
    const { brands, cardsWithBrand } = useRootSelector((state) =>
        getBrandsAndCards(state, allCards)
    ) as unknown as { brands: Option[]; cardsWithBrand: CardWithBrand[] };

    // used for mobile view filters
    const groups = [
        {
            name: 'Card Type',
            options: [allCardsOption, eGift, reload] as Option[],
        },
        {
            name: 'Brands',
            options: brands,
        },
    ];

    return (
        <WalletSortAndFilterView
            result={result.toString()}
            cardsWithBrand={cardsWithBrand}
            setViewableCards={setViewableCards}
            shouldResetFilters={shouldResetFilters}
            shouldPersistFilter={shouldPersistFilters}
        >
            <ResponsiveContainer For={ContainerType.desktop}>
                <FilterAndSortSection brands={brands} />
            </ResponsiveContainer>
            <ResponsiveContainer For={ContainerType.mobile}>
                <MobileSortAndFilterView groups={groups} />
            </ResponsiveContainer>
        </WalletSortAndFilterView>
    );
};
